import { DetailSection } from "@/components/ui/detail-field";
import { useState } from "react";
import { Check, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilaCampo } from "@/components/ui/modal-secciones";
import { mensajeErrorPassword } from "@/lib/erroresPassword";
import { useActivityLogger } from "@/hooks/useActivityLogger";
import { cn } from "@/lib/utils";

function CampoPassword({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="pr-10"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-0 top-0 h-full px-3"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        onClick={() => setVisible(!visible)}
      >
        {visible ? <EyeOff /> : <Eye />}
      </Button>
    </div>
  );
}

/** Seguridad: cambiar contraseña (verifica la actual y luego actualiza). */
export function SeccionSeguridad() {
  const { user } = useAuth();
  const { registrarActualizacion } = useActivityLogger();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const passwordRequirements = [
    { text: "Al menos 8 caracteres", valid: newPassword.length >= 8 },
    { text: "Al menos una mayúscula", valid: /[A-Z]/.test(newPassword) },
    { text: "Al menos una minúscula", valid: /[a-z]/.test(newPassword) },
    { text: "Al menos un número", valid: /[0-9]/.test(newPassword) },
    { text: "Al menos un símbolo especial", valid: /[^A-Za-z0-9]/.test(newPassword) },
  ];
  const allRequirementsMet = passwordRequirements.every((req) => req.valid);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handlePasswordChange = async () => {
    if (!currentPassword) {
      toast.error("Debes ingresar tu contraseña actual");
      return;
    }
    if (!allRequirementsMet) {
      toast.error("La nueva contraseña no cumple con los requisitos");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setIsLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || "",
        password: currentPassword,
      });

      if (signInError) {
        // Un 429 también llega aquí: decir "incorrecta" a quien la tecleó bien
        // lo deja reintentando lo mismo contra el límite.
        toast.error(
          signInError.status === 429
            ? mensajeErrorPassword(signInError)
            : "La contraseña actual es incorrecta",
        );
        setIsLoading(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });

      if (updateError) {
        toast.error(mensajeErrorPassword(updateError));
        setIsLoading(false);
        return;
      }

      await registrarActualizacion("contraseña", null, { origen: "configuracion_usuario" }, "cambiar_password_configuracion");
      toast.success("Contraseña actualizada correctamente");
      resetForm();
    } catch (err) {
      toast.error("Error al cambiar la contraseña");
      await registrarActualizacion(
        "contraseña",
        null,
        { origen: "configuracion_usuario" },
        "cambiar_password_configuracion",
        "error",
        "Error desconocido al cambiar contraseña",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // El formulario se ve directo, sin el paso "Cambiar contraseña": una sola pantalla para hacerlo.
  return (
    // min-h-full + mt-auto: el botón queda al fondo de la sección aunque el formulario sea corto.
    <form
      noValidate
      className="flex min-h-full flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        void handlePasswordChange();
      }}
    >
      <DetailSection
        icon={KeyRound}
        title="Cambiar contraseña"
        description="Te pedimos la actual para confirmar que eres tú."
      >
        <FilaCampo id="currentPassword" label="Contraseña actual">
          <CampoPassword id="currentPassword" value={currentPassword} onChange={setCurrentPassword} placeholder="Tu contraseña actual" autoComplete="current-password" />
        </FilaCampo>
        <FilaCampo id="newPassword" label="Nueva contraseña">
          <CampoPassword id="newPassword" value={newPassword} onChange={setNewPassword} placeholder="La nueva contraseña" autoComplete="new-password" />
          <ul className="mt-2 grid gap-1 text-[12px] sm:grid-cols-2" aria-label="Requisitos de la contraseña">
            {passwordRequirements.map((req) => (
              <li key={req.text} className={cn("flex items-center gap-2", req.valid ? "text-success" : "text-muted-foreground")}>
                {req.valid ? <Check className="size-3.5 shrink-0" aria-label="Cumple" /> : <span aria-hidden className="size-3.5 shrink-0 rounded-full border-2 border-muted-foreground/30" />}
                {req.text}
              </li>
            ))}
          </ul>
        </FilaCampo>
        <FilaCampo id="confirmPassword" label="Confirmar contraseña">
          <CampoPassword id="confirmPassword" value={confirmPassword} onChange={setConfirmPassword} placeholder="Repite la nueva contraseña" autoComplete="new-password" />
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="mt-1 text-[12px] text-destructive">Las contraseñas no coinciden</p>
          )}
        </FilaCampo>

      </DetailSection>
        <div className="mt-auto flex justify-end gap-2.5 pt-6">
          {(currentPassword || newPassword || confirmPassword) && (
            <Button type="button" variant="cancel" size="compact" onClick={resetForm} disabled={isLoading}>
              Limpiar
            </Button>
          )}
          <Button
            type="submit"
            variant="primary-outline"
            size="compact"
            disabled={isLoading || !currentPassword || !allRequirementsMet || newPassword !== confirmPassword}
          >
            {isLoading ? "Guardando..." : "Guardar contraseña"}
          </Button>
        </div>
    </form>
  );
}
